import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import ShopHourForm from './ShopHourForm';
import { isoToUtcInput, utcInputToIso } from '../../utils/utcDateTime';
import 'react-quill/dist/quill.snow.css';

const EditShopHourModal = ({ show, onHide, onUpdate, shopHour, token }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startTime: '',
    endTime: '',
    backgroundImageUrl: '',
    backgroundMobileImageUrl: '',
    backgroundImageBrightness: '',
    backgroundMobileImageBrightness: '',
    backgroundThemeOverride: '',
    backgroundMobileThemeOverride: '',
    shopLogoImageUrl: '',
    shopButtonImageUrl: '',
    shopDescriptionImageUrl: '',
    shopVideoUrl: '',
    shopDescription: '',
    active: false,
    shopHoursPassed: false,
  });
  const [dateError, setDateError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);

  useEffect(() => {
    if (show && shopHour) {
      const startLocal = isoToUtcInput(shopHour.startTime);
      const endLocal = isoToUtcInput(shopHour.endTime);

      setFormData({
        name: shopHour.name || '',
        description: shopHour.description || '',
        startTime: startLocal,
        endTime: endLocal,
        backgroundImageUrl: shopHour.backgroundImageUrl || '',
        backgroundMobileImageUrl: shopHour.backgroundMobileImageUrl || '',
        backgroundImageBrightness:
          shopHour.backgroundImageBrightness !== undefined &&
          shopHour.backgroundImageBrightness !== null
            ? String(shopHour.backgroundImageBrightness)
            : '',
        backgroundMobileImageBrightness:
          shopHour.backgroundMobileImageBrightness !== undefined &&
          shopHour.backgroundMobileImageBrightness !== null
            ? String(shopHour.backgroundMobileImageBrightness)
            : '',
        backgroundThemeOverride: shopHour.backgroundThemeOverride ?? '',
        backgroundMobileThemeOverride: shopHour.backgroundMobileThemeOverride ?? '',
        shopLogoImageUrl: shopHour.shopLogoImageUrl || '',
        shopButtonImageUrl: shopHour.shopButtonImageUrl || '',
        shopDescriptionImageUrl: shopHour.shopDescriptionImageUrl || '',
        shopVideoUrl: shopHour.shopVideoUrl || '',
        shopDescription: shopHour.shopDescription || '',
        active: shopHour.active || false,
        shopHoursPassed: shopHour.shopHoursPassed || false,
      });
      setDateError('');
      setIsSaving(false);
    }
  }, [show, shopHour]);

  const validateDates = () => {
    if (formData.endTime <= formData.startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'startTime' || field === 'endTime') {
      setTimeout(validateDates, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateDates()) return;

    setIsSaving(true);
    try {
      const startUTC = utcInputToIso(formData.startTime);
      const endUTC = utcInputToIso(formData.endTime);

      const normalizeBrightness = value => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const normalizeTheme = value => (value ? value : null);

      const updateData = {
        ...formData,
        startTime: startUTC,
        endTime: endUTC,
        backgroundImageBrightness: normalizeBrightness(formData.backgroundImageBrightness),
        backgroundMobileImageBrightness: normalizeBrightness(
          formData.backgroundMobileImageBrightness
        ),
        backgroundThemeOverride: normalizeTheme(formData.backgroundThemeOverride),
        backgroundMobileThemeOverride: normalizeTheme(formData.backgroundMobileThemeOverride),
      };

      await onUpdate(shopHour.id, updateData);
      onHide();
    } catch (error) {
      console.error('Error updating shop hour:', error);
      alert('Failed to update shop hour. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const saveTemplate = () => {
    const templates = JSON.parse(localStorage.getItem('announcementTemplates') || '[]');
    const templateName = prompt('Enter template name:');
    if (templateName) {
      templates.push({
        name: templateName,
        description: formData.description,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem('announcementTemplates', JSON.stringify(templates));
      alert('Template saved!');
    }
  };

  const loadTemplate = () => {
    const templates = JSON.parse(localStorage.getItem('announcementTemplates') || '[]');
    if (templates.length === 0) {
      alert('No templates found');
      return;
    }
    const templateList = templates.map((t, i) => `${i + 1}. ${t.name}`).join('\n');
    const choice = prompt(`Select template:\n${templateList}\n\nEnter number:`);
    if (choice) {
      const index = parseInt(choice) - 1;
      if (templates[index]) {
        handleChange('description', templates[index].description);
        alert('Template loaded!');
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!videoBusy) onHide();
      }}
      size="xl"
      centered
      dialogClassName="shop-hour-modal"
    >
      <Modal.Header closeButton={!videoBusy}>
        <Modal.Title>Edit Shop Hour</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        <ShopHourForm
          formData={formData}
          onChange={handleChange}
          dateError={dateError}
          onVideoBusyChange={setVideoBusy}
          onSaveTemplate={saveTemplate}
          onLoadTemplate={loadTemplate}
          showShopHoursPassedToggle
          token={token}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={videoBusy}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || videoBusy}
          title={videoBusy ? 'Wait for the video upload to finish' : undefined}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditShopHourModal;
